import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Mail, Unlink, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AutomatedEmailTemplateEditor from "@/components/settings/AutomatedEmailTemplateEditor";
import DocumentsEmailPanel from "@/components/settings/DocumentsEmailPanel";
import EmailChipInput from "@/components/settings/EmailChipInput";
import TrialLocked from "@/components/trial/TrialLocked";
import { TRIAL_DEFAULT_SENDER, useTrialAccount } from "@/hooks/useTrialAccount";
import { ORGANIZATION_SETTINGS_QUERY_KEY } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import {
  getOrganizationSettingsErrorMessage,
  organizationSettingsService,
  type AutomatedEmailTemplate,
  type AutomatedEmailTemplateKey,
  type ContractEmailOAuthProvider,
  type OrganizationSettings,
  type UpdateOrganizationSettingsRequest,
} from "@/services/organizationSettings";

interface AutomatedEmailsCardProps {
  settings: OrganizationSettings | undefined;
  loading: boolean;
  initialTab?: TabKey;
}

type ChildFlag = "decision_emails_enabled" | "funded_emails_enabled" | "accounting_emails_enabled";

/** Always on, so it has no flag: it never counts toward the master switch. */
const DOCUMENTS_TAB = "documents";
type TabKey = ChildFlag | typeof DOCUMENTS_TAB;

interface EmailOption {
  flag: ChildFlag;
  title: string;
  description: string;
  templates: { key: AutomatedEmailTemplateKey; title: string }[];
}

const EMAIL_OPTIONS: EmailOption[] = [
  {
    flag: "decision_emails_enabled",
    title: "Approval and decline",
    description:
      'Sends an "Offer Approved" or "Application Declined" summary, with the decision note, ' +
      "each time an initial underwriting assessment is approved or declined in Funder Review. " +
      "Final underwriting decisions are covered by the funded email.",
    templates: [
      { key: "approved", title: "Offer Approved" },
      { key: "declined", title: "Application Declined" },
    ],
  },
  {
    flag: "funded_emails_enabled",
    title: "Funded",
    description:
      'Sends a "Funding Completed" notice when Final Underwriting marks the deal funded. ' +
      "It is sent once, on the move into Funded.",
    templates: [{ key: "funded", title: "Funding Completed" }],
  },
  {
    flag: "accounting_emails_enabled",
    title: "Accounting",
    description:
      "Sends a funded-deal notice to accounting addresses when Final Underwriting marks " +
      "the deal funded. Independent of the broker funded email.",
    templates: [{ key: "accounting", title: "Accounting funded notice" }],
  },
];

const CHILD_FLAGS: ChildFlag[] = EMAIL_OPTIONS.map((o) => o.flag);
const DEFAULT_TAB: ChildFlag = "decision_emails_enabled";

/** Fixed height + even padding so TabsTrigger's default `pt-1 pb-2.5` cannot skew the chip. */
const tabChipClass = cn(
  "inline-flex h-9 items-center gap-2.5 rounded-full border border-border bg-background",
  "px-3 py-0 text-sm font-medium leading-none text-foreground shadow-sm transition-colors",
  "hover:bg-muted/60 data-[state=active]:border-primary data-[state=active]:bg-primary/10",
  "data-[state=active]:text-foreground after:hidden !pt-0 !pb-0"
);

const AutomatedEmailsCard: React.FC<AutomatedEmailsCardProps> = ({
  settings,
  loading,
  initialTab = DEFAULT_TAB,
}) => {
  const { isTrial } = useTrialAccount();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [savingTemplate, setSavingTemplate] = useState<AutomatedEmailTemplateKey | null>(null);
  const [chipEpoch, setChipEpoch] = useState(0);
  const [copyEmails, setCopyEmails] = useState("");
  const copyEmailsHydratedRef = useRef(false);
  const copyEmailsSavedRef = useRef("");

  const mutation = useMutation({
    mutationFn: (payload: UpdateOrganizationSettingsRequest) =>
      organizationSettingsService.update(payload),
    onMutate: () => setError(null),
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
    },
    onError: (err) => setError(getOrganizationSettingsErrorMessage(err)),
  });

  const connectMutation = useMutation({
    mutationFn: (provider: ContractEmailOAuthProvider) =>
      organizationSettingsService.startContractEmailOAuth(provider),
    onMutate: () => setError(null),
    onSuccess: (data) => {
      window.location.href = data.authorization_url;
    },
    onError: (err) => setError(getOrganizationSettingsErrorMessage(err)),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => organizationSettingsService.disconnectContractEmail(),
    onMutate: () => setError(null),
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
    },
    onError: (err) => setError(getOrganizationSettingsErrorMessage(err)),
  });

  useEffect(() => {
    if (!settings || copyEmailsHydratedRef.current) return;
    copyEmailsHydratedRef.current = true;
    const initial = settings.signed_contract_copy_emails ?? "";
    setCopyEmails(initial);
    copyEmailsSavedRef.current = initial;
  }, [settings]);

  const busy = loading || mutation.isPending;

  const options = useMemo(() => {
    const templates = settings?.automated_email_templates;
    const defaults = settings?.automated_email_defaults;
    if (!templates || !defaults) return EMAIL_OPTIONS;
    return EMAIL_OPTIONS.filter((option) =>
      option.templates.every((template) => templates[template.key] && defaults[template.key])
    );
  }, [settings?.automated_email_templates, settings?.automated_email_defaults]);

  const selectedTab =
    activeTab === DOCUMENTS_TAB || options.some((option) => option.flag === activeTab)
      ? activeTab
      : (options[0]?.flag ?? DEFAULT_TAB);

  const setChild = (flag: ChildFlag, checked: boolean) => {
    const nextChildren = Object.fromEntries(
      CHILD_FLAGS.map((f) => [f, f === flag ? checked : (settings?.[f] ?? false)])
    ) as Record<ChildFlag, boolean>;
    mutation.mutate({
      [flag]: checked,
      // Keep the server master flag in sync with whether any notice is on.
      automated_emails_enabled: Object.values(nextChildren).some(Boolean),
    });
  };

  const saveTemplate = async (key: AutomatedEmailTemplateKey, template: AutomatedEmailTemplate) => {
    setSavingTemplate(key);
    try {
      await mutation.mutateAsync({ automated_email_templates: { [key]: template } });
    } finally {
      setSavingTemplate(null);
    }
  };

  const resetTemplate = (key: AutomatedEmailTemplateKey) =>
    saveTemplate(key, { subject: "", body: "" });

  const saveDocumentsParty = (party: string, next: boolean) =>
    mutation.mutate({ automated_email_parties: { documents: { [party]: next } } });

  const saveAccountingEmails = (emails: string[]) => {
    mutation.mutate(
      { accounting_emails: emails },
      { onError: () => setChipEpoch((epoch) => epoch + 1) }
    );
  };

  const saveCopyEmailsIfDirty = () => {
    if (copyEmails === copyEmailsSavedRef.current) return;
    mutation.mutate(
      {
        esign_provider: "docuseal",
        signed_contract_copy_emails: copyEmails,
      },
      {
        onSuccess: (data) => {
          const next = data.signed_contract_copy_emails ?? "";
          setCopyEmails(next);
          copyEmailsSavedRef.current = next;
        },
      }
    );
  };

  return (
    <Card data-testid="automated-emails-card">
      <CardHeader>
        <CardTitle>Automated Emails</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3 rounded-md border p-3" data-testid="automated-email-address">
          <div className="space-y-0.5">
            <Label>Automated Email Address</Label>
            <p className="text-xs text-muted-foreground">
              {isTrial ? (
                <>
                  Trial accounts always send from{" "}
                  <span className="font-medium text-foreground">{TRIAL_DEFAULT_SENDER}</span>.
                  Connecting a mailbox is available on a full account.
                </>
              ) : (
                <>
                  If no address is connected, emails are sent using noreply@steddy.ai. The connected
                  address is used for sending automated emails and all communication with merchants,
                  brokers, and other parties.
                </>
              )}
            </p>
          </div>

          {settings?.contract_email_reconnect_required && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                The connected mailbox needs to be reconnected — mail could not be sent through it,
                so automated emails from it are paused. Reconnect below to resume them.
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-xs text-muted-foreground">Loading...</p>
          ) : settings?.contract_email_connected ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm">
                Connected as{" "}
                <span className="font-medium text-foreground">
                  {settings.contract_email_address}
                </span>{" "}
                ({settings.contract_email_provider === "outlook" ? "Outlook" : "Gmail"})
              </p>
              <TrialLocked lock="contractSender" inline>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                >
                  <Unlink className="h-4 w-4" />
                  {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect"}
                </Button>
              </TrialLocked>
            </div>
          ) : (
            <TrialLocked lock="contractSender" inline>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => connectMutation.mutate("google")}
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending && connectMutation.variables === "google" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  {connectMutation.isPending && connectMutation.variables === "google"
                    ? "Redirecting..."
                    : "Connect Gmail"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => connectMutation.mutate("microsoft")}
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending && connectMutation.variables === "microsoft" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  {connectMutation.isPending && connectMutation.variables === "microsoft"
                    ? "Redirecting..."
                    : "Connect Outlook"}
                </Button>
              </div>
            </TrialLocked>
          )}
        </div>

        <div
          className="space-y-1.5 rounded-md border p-3"
          data-testid="signed-contract-copy-recipients"
        >
          <Label htmlFor="signed_contract_copy_emails">Signed Contract Copy Recipients</Label>
          <p className="text-xs text-muted-foreground">
            {isTrial
              ? "On a trial account the signed contract PDF is emailed to the account holder only. Extra recipients can be added on a full account."
              : "Comma-separated funder/admin emails that each receive a copy of every signed contract PDF. Choose who else gets the signed documents in the Documents tab below."}
          </p>
          <TrialLocked lock="signedCopyRecipients">
            <Textarea
              id="signed_contract_copy_emails"
              value={copyEmails}
              onChange={(e) => setCopyEmails(e.target.value)}
              onBlur={saveCopyEmailsIfDirty}
              placeholder="funding@example.com, admin@example.com"
              rows={2}
              disabled={loading || mutation.isPending}
            />
          </TrialLocked>
        </div>

        <Tabs
          value={selectedTab}
          onValueChange={(value) => setActiveTab(value as TabKey)}
          className="flex flex-col gap-4"
        >
          <TabsList
            className="flex h-auto w-full flex-col items-stretch gap-2 bg-transparent p-0 pb-2 md:flex-row md:flex-wrap"
            aria-label="Automated email types"
          >
            <TabsTrigger
              value={DOCUMENTS_TAB}
              asChild
              className={tabChipClass}
              data-testid={`automated-email-tab-${DOCUMENTS_TAB}`}
            >
              <div className={cn(tabChipClass, "cursor-pointer justify-between")}>
                <span className="leading-none">Documents</span>
                <Switch
                  id={DOCUMENTS_TAB}
                  checked
                  disabled
                  aria-label="Documents always on"
                  title="Signed documents are always sent"
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                />
              </div>
            </TabsTrigger>
            {options.map((option) => {
              const childOn = settings?.[option.flag] ?? false;
              return (
                <TabsTrigger
                  key={option.flag}
                  value={option.flag}
                  asChild
                  className={tabChipClass}
                  data-testid={`automated-email-tab-${option.flag}`}
                >
                  <div className={cn(tabChipClass, "cursor-pointer justify-between")}>
                    <span className="leading-none">{option.title}</span>
                    <TrialLocked
                      lock="automatedEmails"
                      inline
                      side="left"
                      className="inline-flex items-center"
                    >
                      <Switch
                        id={option.flag}
                        checked={childOn}
                        onCheckedChange={(checked) => setChild(option.flag, checked)}
                        disabled={busy}
                        aria-label={`${option.title} enabled`}
                        data-effective={childOn ? "on" : "off"}
                        onClick={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </TrialLocked>
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent
            value={DOCUMENTS_TAB}
            className="mt-0 min-w-0 flex-1 space-y-4"
            data-testid={`automated-email-option-${DOCUMENTS_TAB}`}
          >
            <p className="text-xs text-muted-foreground">
              Sends the signed documents when a contract or Merchant Tasks flow is completed. It is
              always on. Only the funder side (Me, Accounting and the copy recipients) gets a link
              to the deal in Steddy.
            </p>
            <DocumentsEmailPanel
              picker={settings?.automated_email_pickers?.documents}
              disabled={busy}
              onToggle={saveDocumentsParty}
            />
          </TabsContent>

          {options.map((option) => {
            const recipient =
              settings?.automated_email_recipients?.[option.templates[0].key] ?? "Brokers";
            return (
              <TabsContent
                key={option.flag}
                value={option.flag}
                className="mt-0 min-w-0 flex-1 space-y-4"
                data-testid={`automated-email-option-${option.flag}`}
              >
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="gap-1 font-normal">
                      <Users className="h-3 w-3" />
                      Recipient: {recipient}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>

                {option.flag === "accounting_emails_enabled" && (
                  <div className="space-y-2">
                    <Label htmlFor="accounting_emails">Accounting addresses</Label>
                    <TrialLocked lock="automatedEmails">
                      <EmailChipInput
                        key={chipEpoch}
                        id="accounting_emails"
                        data-testid="accounting-email-chips"
                        value={settings?.accounting_emails ?? []}
                        onChange={saveAccountingEmails}
                        disabled={busy}
                      />
                    </TrialLocked>
                  </div>
                )}

                {settings &&
                  option.templates.map((template, index) => (
                    <div key={template.key} className={index > 0 ? "border-t pt-6" : undefined}>
                      <AutomatedEmailTemplateEditor
                        templateKey={template.key}
                        title={template.title}
                        value={settings.automated_email_templates[template.key]}
                        defaults={settings.automated_email_defaults[template.key]}
                        variables={settings.automated_email_variables[template.key] ?? []}
                        saving={savingTemplate === template.key}
                        onSave={(next) => saveTemplate(template.key, next)}
                        onReset={() => resetTemplate(template.key)}
                      />
                    </div>
                  ))}
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AutomatedEmailsCard;
